/**
 * The State field — `docs/address-state-field.md` §4.1.
 *
 * It was a two-character text box, so the same place arrived as "PA", "Pa.",
 * "Pennsylvania" and "penn". This is a select over the one list in
 * `shared/src/regions.ts`, storing the code and nothing else — which is what
 * makes a State column sort as one thing, group into real places, and offer
 * chips instead of a search box.
 *
 * A wrapper over `MenuSelect` rather than a picker of its own, because §3.6's
 * rule is to add to the kit rather than grow a parallel one. Two things the
 * kit was missing it now has, and both are general: an option can carry a
 * `sub` — the muted rest of its name, so a row reads "PA Pennsylvania" — and
 * a `search` key, so typing "penn" finds PA. Without the second, a menu whose
 * labels are codes could only be searched by somebody who already knew the
 * code, which is the thing a picker exists to save you from.
 *
 * **Not the mockup.** `design/mockups/31-address-state/` is not in the repo,
 * so the measurements here are §4.1's written ones and the design system's,
 * not a pixel reference.
 */
import {
  REGIONS_BY_COUNTRY, regionFieldLabel, type Country,
} from '@rowted/shared'
import { MenuSelect } from '../menu-select.js'

export interface RegionSelectProps {
  value: string
  onChange: (code: string) => void
  /** Which list, and what the field is called. Defaults to the US. */
  country?: Country
  /** The `Field`'s id, so its `<label for>` opens the menu. */
  id?: string
  describedBy?: string
  invalid?: boolean
  disabled?: boolean
  /** Overrides "State" / "Province" — only for a screen reader. */
  label?: string
}

export function RegionSelect({
  value, onChange, country = 'US', id, describedBy, invalid, disabled, label,
}: RegionSelectProps) {
  const options = REGIONS_BY_COUNTRY[country].map((region) => ({
    value: region.code,
    label: region.code,
    sub: region.name,
    // Both, so "pa" and "penn" and "Pennsylvania" all find the same row.
    search: `${region.code} ${region.name}`,
  }))

  return (
    <MenuSelect
      size="field"
      id={id}
      describedBy={describedBy}
      label={label ?? regionFieldLabel(country)}
      value={value || null}
      onChange={(next) => onChange(next ?? '')}
      options={options}
      placeholder={country === 'CA' ? 'Province' : 'State'}
      searchPlaceholder="Type a code or a name"
      disabled={disabled}
      className={invalid ? 'border-danger' : undefined}
      minWidth={240}
    />
  )
}
